import { Box, Typography, Stack, debounce, StackProps } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';

type LineSeriesConfig = {
  key: string;
  name: string;
  color?: string;
  yAxisIndex?: 0 | 1;
  symbolSize?: number;
};

interface DualAxisLineChartProps {
  title: any;
  subTitle?: string;
  isLoading?: boolean;
  data: any[];
  xKey: string;
  containerProps?: StackProps;
  series: LineSeriesConfig[];
  tooltipLabel?: any;
  hasTooltip?: boolean;
  showGridLines?: boolean;
  tooltipFormatter?: (params: any[]) => string;
  leftYAxisLabel?: string;
  rightYAxisLabel?: string;
  leftYAxisFormatter?: (value: number) => string;
  rightYAxisFormatter?: (value: number) => string;
  xAxisLabelFormatter?: (value: string) => string;
  onLineClick?: (params: any) => void;
  chartHeight?: number | string;
}

const DualAxisLineChart = ({
  title,
  subTitle,
  isLoading = false,
  data,
  xKey,
  containerProps,
  series,
  tooltipLabel,
  hasTooltip = true,
  showGridLines = false,
  tooltipFormatter,
  leftYAxisLabel = '',
  rightYAxisLabel = '',
  leftYAxisFormatter,
  rightYAxisFormatter,
  xAxisLabelFormatter,
  onLineClick,
  chartHeight = 350,
}: Readonly<DualAxisLineChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;

  const [windowSize, setWindowSize] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  const chartData = useMemo(() => data ?? [], [data]);

  const options = useMemo(() => {
    if (!chartData.length) {
      return null;
    }

    const axisLineColor =
      colors.widget?.chart?.legendInactiveUpDownIconColor ||
      colors.widget?.chart?.legendTextColor;

    const axisTextColor =
      colors.widget?.chart?.legendTextColor ||
      colors.widget?.text?.main;

    return {
      backgroundColor: colors.widget?.background?.chart,
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: colors.widget?.chart?.tooltipBg,
        borderColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
        borderWidth: 1,
        textStyle: {
          color: colors.widget?.chart?.tooltipText,
          fontSize: 12,
        },
        axisPointer: {
          type: 'line' as const,
          lineStyle: {
            color: axisLineColor,
            type: 'dashed' as const,
            width: 1,
          },
        },
        formatter: tooltipFormatter,
      },
      legend: {
        show: true,
        orient: 'horizontal' as const,
        top: 0,
        left: 0,
        itemWidth: 18,
        itemHeight: 8,
        itemGap: 20,
        textStyle: {
          color: axisTextColor,
          fontSize: 12,
        },
        data: series.map((item) => ({
          name: item.name,
          icon: 'rect',
        })),
      },
      grid: {
        left: 24,
        right: 24,
        top: 48,
        bottom: 24,
        containLabel: true,
      },
      xAxis: {
        type: 'category' as const,
        boundaryGap: false,
        data: chartData.map((item) => item[xKey]),
        axisLabel: {
          color: axisTextColor,
          fontSize: 11,
          formatter: xAxisLabelFormatter,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: axisLineColor,
            type: 'dashed' as const,
            width: 1,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: [
        {
          type: 'value' as const,
          name: leftYAxisLabel,
          position: 'left' as const,
          alignTicks: true,
          nameLocation: 'middle' as const,
          nameGap: 44,
          nameRotate: 90,
          nameTextStyle: {
            color: axisTextColor,
            fontSize: 11,
          },
          axisLabel: {
            color: axisTextColor,
            fontSize: 11,
            formatter: leftYAxisFormatter,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: axisLineColor,
              type: 'dashed' as const,
              width: 1,
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: showGridLines,
            lineStyle: {
              color: colors.widget?.chart?.emptyCircleFill,
              type: 'dashed' as const,
              width: 1,
            },
          },
        },
        {
          type: 'value' as const,
          name: rightYAxisLabel,
          position: 'right' as const,
          alignTicks: true,
          nameLocation: 'middle' as const,
          nameGap: 58,
          nameRotate: -90,
          nameTextStyle: {
            color: axisTextColor,
            fontSize: 11,
          },
          axisLabel: {
            color: axisTextColor,
            fontSize: 11,
            formatter: rightYAxisFormatter,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: axisLineColor,
              type: 'dashed' as const,
              width: 1,
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: series.map((item) => ({
        name: item.name,
        type: 'line' as const,
        yAxisIndex: item.yAxisIndex ?? 0,
        data: chartData.map((dataItem) => dataItem[item.key]),
        smooth: false,
        symbol: 'circle',
        symbolSize: item.symbolSize ?? 8,
        showSymbol: true,
        lineStyle: {
          color: item.color,
          width: 2,
        },
        itemStyle: {
          color: item.color,
        },
        emphasis: {
          focus: 'series' as const,
        },
      })),
      textStyle: {
        fontFamily: 'Montserrat',
      },
    };
  }, [
    chartData,
    colors,
    leftYAxisFormatter,
    leftYAxisLabel,
    rightYAxisFormatter,
    rightYAxisLabel,
    series,
    showGridLines,
    tooltipFormatter,
    xAxisLabelFormatter,
    xKey,
  ]);

  const chartEvents = useMemo(
    () => ({
      click: (params: any) => {
        onLineClick?.(params);
      },
    }),
    [onLineClick]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = debounce(() => {
      setWindowSize(window.innerWidth);
    }, 200);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.clear();
    };
  }, []);

  const renderChartContent = () => {
    if (isLoading) {
      return <LoaderComponent pt={20} />;
    }

    if (!chartData.length || !options) {
      return (
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            variant="bodyText"
            sx={{ color: colors.widget.text.main }}
          >
            No Data Available
          </Typography>
        </Stack>
      );
    }

    return (
      <ReactECharts
        option={options}
        style={{ width: '100%', height: '100%' }}
        onEvents={chartEvents}
      />
    );
  };

  return (
    <WidgetWraper>
      <Box
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...containerProps?.sx,
        }}
        {...containerProps}
      >
        <Stack gap={2} flexShrink={0}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="widgetTitleNew"
              sx={{ color: colors.widget.text.title }}
            >
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && tooltipLabel && (
              <InfoTooltip label={tooltipLabel} />
            )}
          </Stack>

          {subTitle && (
            <Typography
              variant="bodyText"
              sx={{ color: colors.widget.text.title }}
            >
              {subTitle}
            </Typography>
          )}
        </Stack>

        <Box
          mt={1}
          key={windowSize}
          sx={{
            flexGrow: 1,
            minHeight: chartHeight,
            height: 0,
          }}
        >
          {renderChartContent()}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default DualAxisLineChart;
